
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { MatButton } from '@angular/material/button';
import { MatCardActions, MatCardContent } from '@angular/material/card';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';

import { guid } from '@firestitch/common';
import { FsFormModule } from '@firestitch/form';
import { FsMessage } from '@firestitch/message';
import { FsSkeletonModule } from '@firestitch/skeleton';

import { Observable, Subject, forkJoin, of, zip } from 'rxjs';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { AclRoleReferences } from '@common/consts';
import { AclRoleData, EnvironmentData, EnvironmentInviteData, ProjectData } from '@common/data';
import { AclRoleLevel, AclRoleReference } from '@common/enums';
import { Account, AclRole, Environment, Project } from '@common/interfaces';
import { AuthService, SessionService } from '@common/services';

import { EnvironmentCreate } from '../../interfaces';


@Component({
    selector: 'app-invite',
    templateUrl: './invite.component.html',
    styleUrls: ['./invite.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    FsSkeletonModule,
    FormsModule,
    FsFormModule,
    MatCardContent,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    MatCardActions,
    MatButton
]
})
export class InviteComponent implements OnInit, OnDestroy {
  private _cd = inject(ChangeDetectorRef);
  private _router = inject(Router);
  private _environmentData = inject(EnvironmentData);
  private _projectData = inject(ProjectData);
  private _authService = inject(AuthService);
  private _sessionService = inject(SessionService);
  private _environmentInviteData = inject(EnvironmentInviteData);
  private _message = inject(FsMessage);
  private _aclRoleData = inject(AclRoleData);


  @Input() public environmentCreate: EnvironmentCreate = null;

  @Output() public completed = new EventEmitter();

  public aclRoleReferences = AclRoleReferences;
  public account: Account = null;

  private _destroy$ = new Subject<void>();

  public ngOnInit(): void {
    this.account = this._sessionService.account();
    this.memberAdd(3);
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public focus(index: number): void {
    if (index === (this.environmentCreate.members.length - 1)) {
      this.memberAdd();
    }
  }

  public memberAdd(count: number = 1): void {
    for (let i = 0; i < count; i++) {
      this.environmentCreate.members.push({
        email: null,
        aclRoleReference: AclRoleReference.EnvironmentAdmin,
        guid: guid(),
      });
    }

    this._cd.detectChanges();
  }

  public save = () => {
    return this._environmentData.post({ name: this.environmentCreate.workspaceName })
      .pipe(
        switchMap((environment: Environment) => {
          return zip(
            this._authService.reloadPermissions(),
            this._environmentData.switch(this.account.id, environment.id),
          )
            .pipe(
              map(() => {
                this._sessionService.environment(environment);

                return environment;
              }),
              takeUntil(this._destroy$),
            );
        }),
        switchMap((environment: Environment) => {
          return this._projectData.post({ name: this.environmentCreate.projectName })
            .pipe(
              takeUntil(this._destroy$),
            )
            .pipe(
              map((project: Project) => ({
                environment,
                project,
              })),
            );
        }),
        switchMap((data) => this._saveInvites(data.environment, data.project)),
      )
      .pipe(
        tap(() => {
          this._message.success('Your workspace is ready');
          this._router.navigate(['/projects']);
          this.completed.emit();
        }),
        takeUntil(this._destroy$),
      );
  };

  private _saveInvites(environment: Environment, project: Project): Observable<Project> {
    return this._aclRoleData.gets({
      environmentId: environment.id,
      reference: AclRoleReferences.map((item) => {
        return item.value;
      }).join(','),
    })
      .pipe(
        switchMap((aclRoles) => {
          const queries$ = [];
          this.environmentCreate.members
            .filter((item) => item.email && item.aclRoleReference)
            .forEach((member) => {
              const selectedRole = aclRoles
                .find((role: AclRole) => {
                  return role.reference === member.aclRoleReference;
                });

              const objectId = selectedRole?.level === AclRoleLevel.Environment ?
                environment.id : project.id;

              queries$.push(this._environmentInviteData.post(environment.id, {
                email: member.email,
                objects: [
                  {
                    aclRoleIds: selectedRole ? [selectedRole.id] : [],
                    objectId,
                  },
                ],
              }));
            });

          if (!queries$.length) {
            queries$.push(of(true));
          }

          return forkJoin(...queries$)
            .pipe(
              map(() => project),
              takeUntil(this._destroy$),
            );
        }),
      );
  }

}
